import { useState, useMemo, useReducer } from "react";
import { Row, Col, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';
import moment from "moment";

//components
import TableWidget from '../../TableWidget.js';
import OvertimeMapper from '../OvertimeMapper.js'

//elements
import Select from '~/elements/Select.js';
import { Input, NumberInput } from "~/elements/Input.js";
import RequiredStar from '~/elements/RequiredStar.js';
import Button from '~/components/CustomButton/CustomButton';
import DatePicker from '~/components/DatePicker/DatePicker.jsx';
import SwitchWidget from '../../SwitchWidget.js';
import LoadingSpinner from '~/elements/LoadingSpinner';

//utils
import { validateRequestPayload, resolveWageTypeCodeLabel } from '~/utils/commonMethods';
import { getWeekInCycleOptions, isSunday, isMonday, DEFAULT_OVERTIME_WAGE_TYPE } from '~/utils/constants';
import { timeFormat, getShortDateFormat } from '~/utils/dateFormat';


const AddEditOvertimeSetting = ({ values, updateData, cancel, payroll_wagetypes, t, integrationType, commonStore }) => {
    const payrollWageTypeOptions = payroll_wagetypes.map(item => ({ value: item.value, label: item.label }));
    const defaultOvertimeWageType = resolveWageTypeCodeLabel(DEFAULT_OVERTIME_WAGE_TYPE[integrationType], commonStore);
    const [name, setName] = useState(values?.name);
    const [overtime, setOvertime] = useState(values?.overtime);
    const [integrated_wagetype, setIntegrated_wagetype] = useState(values?.integrated_wagetype);
    const [hourly_wagetype, setHourly_wagetype] = useState(values?.hr_int_wagetype);
    const ratio = useMemo(() => {
        if (!overtime || isNaN(overtime)) return "";
        const ratioValue = (parseFloat(overtime) / 100) + 1;
        return `1:${parseFloat(ratioValue.toFixed(4))}`;
    }, [overtime]);

    const saveData = () => {
        const data = {
            id: values?.id,
            name: name,
            overtime: overtime,
            integrated_wagetype: integrated_wagetype,
            hr_int_wagetype: hourly_wagetype || null,
            ratio: ratio
        }

        const validFields = ["name", "overtime", "ratio"];
        const validMessage = ["Name", "Overtime", "Ratio"];
        const errors = validateRequestPayload(data, validFields, validMessage, t);
        if (errors) {
            updateData({ hasError: true, errors: errors })
        } else {
            updateData(data)
        }
    }


    return (
        <>
            <Row className="mt-4">
                <Col sm={3} xs={4}>
                    <label className="control-label">{t('Name')} <RequiredStar /></label>
                    <Input value={name} placeholder={t("Enter Name")} onChange={(e) => setName(e.target.value)} />
                </Col>
                <Col sm={3} xs={4}>
                    <label className="control-label">{t('Overtime')} % <RequiredStar /></label>
                    <NumberInput value={overtime} placeholder={t("Overtime % ex: 40.5")}
                        onChange={e => setOvertime(e.target.value)} />
                </Col>
                <Col sm={4} xs={6}>
                    <label className="control-label">{`${t('Payroll')} ${t('Wage Type')}`} ({t('Fixed Salary')})</label>
                    <Select
                        options={payrollWageTypeOptions}
                        value={integrated_wagetype && payrollWageTypeOptions.find(item => item.value === integrated_wagetype.value) || null}
                        onChange={selectedItem => {
                            setIntegrated_wagetype(selectedItem)
                        }}
                        isClearable
                        placeholder={`${t('Select')} ${t('Payroll')} ${t('Wage Type')}`}
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col sm={4} xs={6} className="mt-3">
                    <label className="control-label">{`${t('Payroll')} ${t('Wage Type')}`} ({t('Hourly')})</label>
                    <Select
                        options={payrollWageTypeOptions}
                        value={hourly_wagetype && payrollWageTypeOptions.find(item => item.value === hourly_wagetype.value) || null}
                        onChange={selectedItem => {
                            setHourly_wagetype(selectedItem)
                        }}
                        isClearable
                        placeholder={`${t('Select')} ${t('Payroll')} ${t('Wage Type')}`}
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col sm={4} xs={6} className="mt-3">
                    <label className="control-label">{t('Ratio')} <RequiredStar /></label>
                    <Input value={ratio} disabled placeholder={t("Auto-calcuted from Overtime %")} />
                </Col>
            </Row>
            {defaultOvertimeWageType &&
                <Row>
                    <Col sm={12}>
                        <p className='font-size-12 fw-bold'><RequiredStar /><RequiredStar />
                            {t('If left blank, the system will use its built-in default wage type')}: "{defaultOvertimeWageType}".
                        </p>
                    </Col>
                </Row>
            }
            <div className="position-absolute" style={{ bottom: 10 }}>
                <Button wd me2 fill onClick={() => saveData()}>{t("Save")}</Button>
                <Button wd ms2 onClick={() => cancel()}>{t("Cancel")}</Button>
            </div>
        </>
    )
}

const buildDefaultWeeklyOvertimeState = (values = {}) => ({
    name: values.name ?? "",
    weeklyOvertimeLimit: values.weekly_overtime_limit ?? 40,
    validFrom: values.valid_from ?? null,
    validTo: values.valid_to ?? null,
    averageOvertimeEnabled: values.allow_overtime_averaging ?? false,
    weeksInCycle: values.week_length ?? null,
    cyclicWeeklyOvertimeCap: values.average_overtime_weekly_cap ?? 40,
    isActive: !!values.is_active,
    isSystemDefault: !!values.is_system_default,
    includeTimebank: !!values.include_timebank,
    hasError: false,
    errors: {},
});

const WEEKLY_OVERTIME_ACTIONS = {
    SET_FIELD: "SET_FIELD",
    TOGGLE_AVERAGE_OVERTIME: "TOGGLE_AVERAGE_OVERTIME",
    SET_ERRORS: "SET_ERRORS",
    RESET: "RESET",
};

const weeklyOvertimeReducer = (state, action) => {
    switch (action.type) {
        case WEEKLY_OVERTIME_ACTIONS.SET_FIELD:
            return { ...state, [action.field]: action.value, hasError: false };

        case WEEKLY_OVERTIME_ACTIONS.TOGGLE_AVERAGE_OVERTIME:
            return {
                ...state,
                averageOvertimeEnabled: action.value,
                ...(action.value === false && {
                    weeksInCycle: null,
                    cyclicWeeklyOvertimeCap: 40,
                }),
            };

        case WEEKLY_OVERTIME_ACTIONS.RESET:
            return buildDefaultWeeklyOvertimeState(action.values);

        default:
            return state;
    }
};

const DefaultWeeklyOvertimeList = (overtimeList) => {
    const overtime = overtimeList[0];
    return [
        {
            id: uuid(),
            weekly_overtime_id: 0,
            from: 0,
            to: 1439,
            Overtime_Setting: overtime,
            overtime_setting_id: overtime.id,
            name: overtime.name
        }
    ]
}

const formatData = (data, dateTimeRules) => {
    const formattedData = [];
    data?.forEach(e => {
        formattedData.push({
            name: e.Overtime_Setting.name,
            from: timeFormat(e.from, dateTimeRules),
            to: timeFormat(e.to, dateTimeRules),
        })
    })
    return formattedData;
}

export const AddEditWeeklyOvertimeSetting = ({ values, updateData, cancel, t, overtimeSettingList,
    dateTimeRules, clientStore, commonStore, handleDelete, ruleActivation }) => {
    const overtimeOptions = overtimeSettingList?.map((o) => ({ value: o.id, label: o.name }));
    const weeksInCycleOptions = useMemo(() => getWeekInCycleOptions(t, 2), [t]);

    const [state, dispatch] = useReducer(weeklyOvertimeReducer, values, buildDefaultWeeklyOvertimeState);
    const [weeklyOvertimeMap, setweeklyOvertimeMap] = useState(values.Weekly_Overtime_Maps || DefaultWeeklyOvertimeList(overtimeSettingList));
    const [showOvertimeForm, setShowOvertimeForm] = useState(false);
    const [currentOvertimeSetting, setCurrentOvertimeSetting] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const isNotEditable = values.isExpired || state.isSystemDefault || state.isActive;
    const isExpired = values.isExpired || false;
    const isNewSetting = !values || !values.id;

    const set = (field) => (value) =>
        dispatch({ type: WEEKLY_OVERTIME_ACTIONS.SET_FIELD, field, value });

    const setFromEvent = (field) => (e) =>
        dispatch({ type: WEEKLY_OVERTIME_ACTIONS.SET_FIELD, field, value: e.target.value });

    const saveData = () => {
        const validFields = ["name", "weeklyOvertimeLimit"];
        const validMessages = ["Rule Name", "Weekly Overtime Limit"];
        if (state.averageOvertimeEnabled) {
            validFields.push("weeksInCycle", "cyclicWeeklyOvertimeCap");
            validMessages.push("Weekly Overtime Cycle", "Cyclic Weekly Overtime Cap");
        }
        const errors = validateRequestPayload(state, validFields, validMessages, t);
        if (errors)
            return updateData({ hasError: true, errors: errors });
        updateData({
            name: state.name,
            weekly_overtime_limit: state.weeklyOvertimeLimit,
            valid_from: state.validFrom ? moment(state.validFrom).format("YYYY-MM-DD") : null,
            valid_to: state.validTo ? moment(state.validTo).format("YYYY-MM-DD") : null,
            allow_overtime_averaging: state.averageOvertimeEnabled,
            week_length: state.averageOvertimeEnabled ? state.weeksInCycle.value : 1,
            average_overtime_weekly_cap: state.cyclicWeeklyOvertimeCap,
            weeklyOvertimeMap
        });
    };

    const handleWeeklyOvertimeDelete = (idx) => {
        const weeklyOvertime = weeklyOvertimeMap[idx];
        if (!weeklyOvertime) return;
        setLoading(true);
        deleteOvertime(weeklyOvertime, idx);
    }

    const handleWeeklyOvertime = (data) => {
        try {
            if (data.hasError)
                return commonStore.addNotification(t(data.errors[0].error), null, 'warning');
            data.weekly_overtime_id = values.id || 0;
            data.Overtime_Setting = overtimeSettingList.find(e => e.id === data.overtime_setting_id);
            if (!data.id && !values.id)
                data.id = uuid();
            setLoading(true);
            saveWeeklyOvertimeMap(data);
        } catch (err) {
            return commonStore.addNotification(t(err.message || 'Something went wrong!'), null, 'error');
        }
    }

    const handleWeeklyOvertimeForm = (index, isAdd = false) => {
        if (index === null && !isAdd) {
            setCurrentOvertimeSetting(null);
            setShowOvertimeForm(false);
        } else {
            const data = { id: null, overtime_setting_id: null, from: 0, to: 0 };
            if (!isAdd) {
                const weeklyOvertime = weeklyOvertimeMap[index];
                data.id = weeklyOvertime.id;
                data.overtime_setting_id = weeklyOvertime.overtime_setting_id;
                data.from = weeklyOvertime.from;
                data.to = weeklyOvertime.to;
            }
            setCurrentOvertimeSetting(data);
            setShowOvertimeForm(true);
        }
    }

    const deleteOvertime = (weeklyOvertime, idx) => {
        const { id } = weeklyOvertime;
        handleDelete(id, async (id) => {
            try {
                if (id && values.id)
                    await clientStore.deleteWeeklyOvertimeMap(id);
                const newWeeklyOvertimeMap = [...weeklyOvertimeMap];
                newWeeklyOvertimeMap.splice(idx, 1);
                setweeklyOvertimeMap(newWeeklyOvertimeMap);
                setLoading(false);
            } catch (err) {
                setLoading(false);
                console.error(err);
            }
        }, null, () => { setLoading(false); });
    }

    const saveWeeklyOvertimeMap = async (data) => {
        try {
            let result, currentWeeklyOvertime = data;
            if (values.id) {
                if (data.id)
                    result = await clientStore.updateWeeklyOvertimeMap(data.id, data);
                else
                    result = await clientStore.saveWeeklyOvertimeMap(data);
                const { weeklyOvertimeMap } = result;
                currentWeeklyOvertime = weeklyOvertimeMap;
            }
            const __weeklyOvertimeMap = weeklyOvertimeMap.filter(e => e.id !== currentWeeklyOvertime.id);
            __weeklyOvertimeMap.push(currentWeeklyOvertime);
            setweeklyOvertimeMap(__weeklyOvertimeMap);
            setLoading(false);
            handleWeeklyOvertimeForm(null, false);
            return commonStore.addNotification(t('Successfully added overtime'), null, 'success');
        } catch (err) {
            setLoading(false);
        }
    }

    if (isLoading) return <LoadingSpinner />

    return (
        <Row className="p-2">
            <Col xs={6}>
                <Row className='mt-2'>
                    <label className="control-label mt-2 font-color-primary">{t('Weekly Overtime')}</label>
                </Row>
                <hr />
                <Row className='align-items-center mb-2'>
                    <Col xs={7}>
                        <label className="control-label">{t('Weekly Overtime Rule Name')} <RequiredStar />
                            <OverlayTrigger placement="right" overlay={<Tooltip id="tooltip-top">{t("The name used to identify this weekly overtime rule")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={5}>
                        <Input
                            value={state.name}
                            placeholder={t("Enter rule name")}
                            onChange={setFromEvent("name")}
                            disabled={isNotEditable}
                        />
                    </Col>
                </Row>
                <Row className='align-items-center mb-4'>
                    <Col xs={8}>
                        <label className="control-label">{t('Weekly Overtime Limit')} <RequiredStar />
                            <OverlayTrigger placement="right" overlay={<Tooltip id="tooltip-top">{t("The maximum number of normal hours an employee can work in a week before overtime kicks in")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={4}>
                        <NumberInput
                            value={state.weeklyOvertimeLimit}
                            placeholder={t("Enter hours ex") + ": 40"}
                            onChange={setFromEvent("weeklyOvertimeLimit")}
                            disabled={isNotEditable}
                        />
                    </Col>
                </Row>
                <hr />
                <Row className='mt-2'>
                    <label className="control-label my-2 font-color-primary">{t('Rule Validity')}</label>
                </Row>
                <hr />
                <Row className='align-items-center mb-1'>
                    <Col xs={6}>
                        <label className="control-label">{t('Valid From')}
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The date from which this rule becomes effective")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                        <DatePicker
                            selected={state.validFrom ? new Date(moment(state.validFrom).format('MM/DD/YYYY')) : null}
                            placeholder={t('Select date')}
                            dateFormat={getShortDateFormat(dateTimeRules)}
                            onChange={set("validFrom")}
                            filterDate={isMonday}
                            disabled={isNotEditable}
                        />
                    </Col>
                    <Col xs={6}>
                        <label className="control-label">{t('Valid To')}
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The date until which this rule is effective. Leave empty for an open-ended rule")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                        <DatePicker
                            selected={state.validTo ? new Date(moment(state.validTo).format('MM/DD/YYYY')) : null}
                            placeholder={t('Select date')}
                            dateFormat={getShortDateFormat(dateTimeRules)}
                            onChange={set("validTo")}
                            disabled={isNotEditable}
                            filterDate={isSunday}
                            minDate={state.validFrom ? new Date(moment(state.validFrom).endOf('isoWeek').format('MM/DD/YYYY')) : null}
                        />
                    </Col>
                </Row>
            </Col>
            <Col xs={6}>
                <Row className='mt-2'>
                    <label className="control-label mt-2 font-color-primary">{t('Average Overtime Settings')} <b className="text-muted">({t('Not Available')})</b></label>
                </Row>
                <hr />
                <Row className='align-items-center mb-2'>
                    <Col xs={8}>
                        <label className="control-label">{t('Average Overtime')}
                            <OverlayTrigger key="ave_overtime" placement="right" overlay={<Tooltip id="tooltip-top">{t("Enable average overtime to calculate overtime over a cycle of weeks instead of a single week")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={4}>
                        <SwitchWidget
                            value={state.averageOvertimeEnabled}
                            disabled
                            onChange={(checked) =>
                                dispatch({ type: WEEKLY_OVERTIME_ACTIONS.TOGGLE_AVERAGE_OVERTIME, value: checked })
                            }
                        />
                    </Col>
                </Row>
                <Row className='align-items-center mb-1'>
                    <Col xs={6}>
                        <Row className='align-items-center mb-1'>
                            <Col xs={10}>
                                <label className="control-label">{t('Weekly Overtime Cycle')} <RequiredStar />
                                    <OverlayTrigger key="ave_overtime" placement="top" overlay={<Tooltip id="tooltip-top">{t("The number of weeks in the overtime averaging cycle. Overtime is calculated as an average over this period")}</Tooltip>}>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={10}>
                                <Select
                                    options={weeksInCycleOptions}
                                    value={state.weeksInCycle}
                                    onChange={set("weeksInCycle")}
                                    style={{ width: '100%' }}
                                    isDisabled={!state.averageOvertimeEnabled}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={6}>
                        <Row className='align-items-center mb-1'>
                            <Col xs={10}>
                                <label className="control-label">{t('Cyclic Weekly Overtime Cap')} <RequiredStar />
                                    <OverlayTrigger key="ave_overtime" placement="top" overlay={<Tooltip id="tooltip-top">{t("The maximum average overtime hours allowed per week across the entire cycle")}</Tooltip>}>
                                        <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </label>
                            </Col>
                            <Col xs={10}>
                                <NumberInput
                                    value={state.cyclicWeeklyOvertimeCap}
                                    placeholder={t("Enter hours ex") + ": 50"}
                                    onChange={set("cyclicWeeklyOvertimeCap")}
                                    disabled={!state.averageOvertimeEnabled}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
                {/* <hr />
                <Row className='mt-2'>
                    <label className="control-label my-2 font-color-primary">{t('Timebank Configuration')}</label>
                </Row>
                <hr />
                <Row className='align-items-center my-2'>
                    <Col xs={8}>
                        <label className="control-label">{t('Deduct timebank hours from timesheet')}
                            <OverlayTrigger key="ave_overtime" placement="right" overlay={<Tooltip id="tooltip-top">{t("Deduct timebank hours from timesheet before calculating weekly overtime")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={4}>
                        <SwitchWidget
                            value={state.includeTimebank}
                            disabled={isNotEditable}
                            onChange={set('includeTimebank')}
                        />
                    </Col>
                </Row> */}
            </Col>
            {isNotEditable &&
                <Col xs={12} className="d-flex justify-content-start my-2">
                    <span className="fw-bold">{t("Note")}:&nbsp;</span>
                    <span className={"fw-bold" + (state.isActive ? " text-success" : " text-danger")} style={{ fontStyle: "italic" }}>
                        {isExpired ? t("This is an expired setting. Cannot be edited.") : ""}
                        {state.isSystemDefault ? t("This is a system default setting. Cannot be edited.") : ""}
                        {state.isActive ? t("Rule is active and can be assigned to users.") : ""}
                    </span>
                </Col>
            }
            <Col xs={12}>
                <hr />
                <Row className='mt-4 align-items-center'>
                    <Col xs={8}>
                        <label className="font-size-15 fw-bold mt-2 font-color-primary">{t('Overtime Settings')}</label>
                    </Col>
                    <Col xs={4} className='d-flex justify-content-end'>
                        <Button wd fill onClick={() => handleWeeklyOvertimeForm(true, true)}>
                            <i className="fa fa-plus-circle fa-xl margin-right-10" />
                        </Button>
                    </Col>
                </Row>
                <Row className='align-items-center mb-2'>
                    <Col xs={12}>
                        <TableWidget
                            headers={['Overtime', 'From', 'To']}
                            metaData={[
                                { field: 'name', type: 'text' },
                                { field: 'from', type: 'text' },
                                { field: 'to', type: 'text' }
                            ]}
                            data={formatData(weeklyOvertimeMap, dateTimeRules)}
                            minRemoveIndex={0}
                            onChange={_ => { }}
                            page={'weeklyOvertimeMap'}
                            customDeleteHandler={handleWeeklyOvertimeDelete}
                            customFormHandler={(index) => handleWeeklyOvertimeForm(index, false)}
                        />
                    </Col>
                </Row>
            </Col>
            <Col xs={8} className="position-sticky" style={{ bottom: 0 }} >
                {!isNotEditable &&
                    <Button wd me2 fill onClick={saveData}>{t("Save")}</Button>
                }
                {!isExpired && !isNewSetting &&
                    <>
                        {state.isActive ?
                            <>
                                {state.isSystemDefault ? null :
                                    <Button wd ms2 onClick={() => ruleActivation(values.id, "inactive")} fill danger>{t("Deactivate")}</Button>
                                }
                            </>
                            :
                            <Button wd ms2 onClick={() => ruleActivation(values.id, "active")} fill success>{t("Activate")}</Button>
                        }
                    </>
                }
                <Button wd ms2 onClick={cancel}>{t("Cancel")}</Button>
            </Col>
            {showOvertimeForm &&
                <OvertimeMapper
                    t={t}
                    values={currentOvertimeSetting}
                    dateTimeRules={dateTimeRules}
                    submit={(data) => handleWeeklyOvertime(data)}
                    cancel={() => handleWeeklyOvertimeForm(null, false)}
                    overtimeSettingList={overtimeOptions}
                />
            }
        </Row>
    );
};

const buildDefaultOvertimeCapsState = (values = {}) => ({
    name: values.name ?? "",
    dailyOvertimeCap: values.daily_overtime_cap ?? 5,
    weeklyOvertimeCap: values.weekly_overtime_cap ?? 10,
    averageWeeklyOvertimeCap: values.average_weekly_overtime_cap ?? 25,
    yearlyOvertimeCap: values.yearly_overtime_cap ?? 200,
    validFrom: values.valid_from ?? null,
    validTo: values.valid_to ?? null,
    isActive: values.is_active ?? false,
    isSystemDefault: values.is_system_default ?? false,
    hasError: false,
    errors: {},
});

const OVERTIME_CAPS_ACTIONS = {
    SET_FIELD: "SET_FIELD",
    SET_ERRORS: "SET_ERRORS",
    RESET: "RESET",
};

const overtimeCapsReducer = (state, action) => {
    switch (action.type) {
        case OVERTIME_CAPS_ACTIONS.SET_FIELD:
            return { ...state, [action.field]: action.value, hasError: false };

        case OVERTIME_CAPS_ACTIONS.SET_ERRORS:
            return { ...state, hasError: true, errors: action.errors };

        case OVERTIME_CAPS_ACTIONS.RESET:
            return buildDefaultOvertimeCapsState(action.values);

        default:
            return state;
    }
};


export const AddEditOvertimeCaps = ({ values, updateData, cancel, t, dateTimeRules, ruleActivation }) => {
    const [state, dispatch] = useReducer(overtimeCapsReducer, values, buildDefaultOvertimeCapsState);

    const isNotEditable = values.isExpired || state.isSystemDefault || state.isActive;
    const isExpired = values.isExpired || false;
    const isNewSetting = !values || !values.id;

    const set = (field) => (value) =>
        dispatch({ type: OVERTIME_CAPS_ACTIONS.SET_FIELD, field, value });

    const setFromEvent = (field) => (e) =>
        dispatch({ type: OVERTIME_CAPS_ACTIONS.SET_FIELD, field, value: e.target.value });

    const setEvent = (field) => (e) =>
        dispatch({ type: OVERTIME_CAPS_ACTIONS.SET_FIELD, field, value: e.target.value });

    const saveData = () => {
        const validFields = ["name", "dailyOvertimeCap", "weeklyOvertimeCap", "averageWeeklyOvertimeCap", "yearlyOvertimeCap"];
        const validMessages = ["Rule Name", "Daily Overtime Cap", "Weekly Overtime Cap", "Average Weekly Overtime Cap", "Yearly Overtime Cap"];

        const errors = validateRequestPayload(state, validFields, validMessages, t);
        if (errors)
            return updateData({ hasError: true, errors });

        updateData({
            name: state.name,
            daily_overtime_cap: state.dailyOvertimeCap,
            weekly_overtime_cap: state.weeklyOvertimeCap,
            average_weekly_overtime_cap: state.averageWeeklyOvertimeCap,
            yearly_overtime_cap: state.yearlyOvertimeCap,
            valid_from: state.validFrom ? moment(state.validFrom).format("YYYY-MM-DD") : null,
            valid_to: state.validTo ? moment(state.validTo).format("YYYY-MM-DD") : null,
            is_active: state.isActive,
        });
    };

    return (
        <Row>
            <Col xs={6}>
                <Row className='mt-2'>
                    <label className="control-label mt-2 font-color-primary">{t('Overtime Cap Details')}</label>
                </Row>
                <hr />
                <Row className='align-items-center mb-2'>
                    <Col xs={7}>
                        <label className="control-label">{t('Rule Name')} <RequiredStar />
                            <OverlayTrigger placement="right" overlay={<Tooltip id="tooltip-top">{t("The name used to identify this overtime cap rule")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={5}>
                        <Input
                            value={state.name}
                            placeholder={t("Enter rule name")}
                            onChange={setFromEvent("name")}
                            disabled={isNotEditable}
                        />
                    </Col>
                </Row>
            </Col>
            <Col xs={6}>
                <Row className='mt-2'>
                    <label className="control-label mt-2 font-color-primary">{t('Rule Validity')}</label>
                </Row>
                <hr />
                <Row className='align-items-center mb-1'>
                    <Col xs={6}>
                        <label className="control-label">{t('Valid From')}
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The date from which this overtime cap rule becomes effective")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                        <DatePicker
                            selected={state.validFrom ? new Date(moment(state.validFrom).format('MM/DD/YYYY')) : null}
                            placeholder={t('Select date')}
                            dateFormat={getShortDateFormat(dateTimeRules)}
                            onChange={set("validFrom")}
                            disabled={isNotEditable}
                        />
                    </Col>
                    <Col xs={6}>
                        <label className="control-label">{t('Valid To')}
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The date until which this overtime cap rule is effective. Leave empty for an open-ended rule")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                        <DatePicker
                            selected={state.validTo ? new Date(moment(state.validTo).format('MM/DD/YYYY')) : null}
                            placeholder={t('Select date')}
                            dateFormat={getShortDateFormat(dateTimeRules)}
                            onChange={set("validTo")}
                            disabled={isNotEditable}
                            minDate={state.validFrom ? new Date(moment(state.validFrom).add(1, 'days').format('MM/DD/YYYY')) : null}
                        />
                    </Col>
                </Row>
            </Col>
            <Col xs={6}>
                <Row className='mt-2'>
                    <label className="control-label mt-2 font-color-primary">{t('Capped Limits')}</label>
                </Row>
                <hr />
                <Row className='align-items-center mb-1'>
                    <Col xs={10}>
                        <label className="control-label">{t('Daily Overtime Cap Limit')} <RequiredStar />
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The maximum overtime hours an employee is allowed to work in a single day")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={2}>
                        <NumberInput
                            value={state.dailyOvertimeCap}
                            placeholder={t("Enter hours ex") + ": 5"}
                            onChange={setEvent("dailyOvertimeCap")}
                            disabled={isNotEditable}
                        />
                    </Col>
                </Row>
                <Row className='align-items-center mb-1'>
                    <Col xs={10}>
                        <label className="control-label">{t('Weekly Overtime Cap Limit')} <RequiredStar />
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The maximum overtime hours an employee is allowed to work in a single week")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={2}>
                        <NumberInput
                            value={state.weeklyOvertimeCap}
                            placeholder={t("Enter hours ex") + ": 10"}
                            onChange={setEvent("weeklyOvertimeCap")}
                            disabled={isNotEditable}
                        />
                    </Col>
                </Row>
                <Row className='align-items-center mb-1'>
                    <Col xs={10}>
                        <label className="control-label">{t('Average Weekly Overtime Cap Limit')} <RequiredStar />
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The maximum average overtime hours per week allowed over a multi-week cycle")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={2}>
                        <NumberInput
                            value={state.averageWeeklyOvertimeCap}
                            placeholder={t("Enter hours ex") + ": 25"}
                            onChange={setEvent("averageWeeklyOvertimeCap")}
                            disabled={isNotEditable}
                        />
                    </Col>
                </Row>
                <Row className='align-items-center mb-1'>
                    <Col xs={10}>
                        <label className="control-label">{t('Yearly Overtime Cap Limit')} <RequiredStar />
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">{t("The maximum total overtime hours an employee is allowed to accumulate in a year")}</Tooltip>}>
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </label>
                    </Col>
                    <Col xs={2}>
                        <NumberInput
                            value={state.yearlyOvertimeCap}
                            placeholder={t("Enter hours ex") + ": 200"}
                            onChange={setEvent("yearlyOvertimeCap")}
                            disabled={isNotEditable}
                        />
                    </Col>
                </Row>
            </Col>

            {isNotEditable &&
                <Col xs={12} className="d-flex justify-content-start my-2">
                    <span className="fw-bold">{t("Note")}:&nbsp;</span>
                    <span className={"fw-bold" + (state.isActive ? " text-success" : " text-danger")} style={{ fontStyle: "italic" }}>
                        {isExpired ? t("This is an expired setting. Cannot be edited.") : ""}
                        {state.isSystemDefault ? t("This is a system default setting. Cannot be edited.") : ""}
                        {state.isActive ? t("Rule is active and can be assigned to users") : ""}
                    </span>
                </Col>
            }

            <div className="position-absolute" style={{ bottom: 10, maxWidth: '80%' }}>
                {!isNotEditable &&
                    <Button wd me2 fill onClick={saveData}>{t("Save")}</Button>
                }
                {!isExpired && !isNewSetting &&
                    <>
                        {state.isActive ?
                            <>
                                {state.isSystemDefault ? null :
                                    <Button wd ms2 onClick={() => ruleActivation(values.id, "inactive")} fill danger>{t("Deactivate")}</Button>
                                }
                            </>
                            :
                            <Button wd ms2 onClick={() => ruleActivation(values.id, "active")} fill success>{t("Activate")}</Button>
                        }
                    </>
                }
                <Button wd ms2 onClick={cancel}>{t("Cancel")}</Button>
            </div>
        </Row>
    );
};


export default AddEditOvertimeSetting;
